import PageHeader from '@/components/layout/PageHeader';
import React, { useState, useMemo, useEffect } from 'react';
import ExportJsonExcel from 'js-export-excel';
import { getListPayload } from '@/scripts/utils';
import { SEARCH_ITEM_COL, SEARCH_ITEM_GUTTER } from '@/scripts/config';
import { Button, Card, Col, Space, Dropdown, DatePicker, Input, Form, message, Row, Select, Tag, Table, Popover } from 'antd';
import { useGetEndPortQuery, useGetStartPortQuery } from '@/store/apiSlice';
import { createJsonOptions, getLabelFromMap } from '@/scripts/utils';
import ajax from '@/scripts/ajax';
import dayjs from 'dayjs';
import locale from 'antd/es/date-picker/locale/zh_CN';
import 'dayjs/locale/zh-cn';

const pageKey = 'OrderList';
export default function OrderList() {
    // hooks
    const [form] = Form.useForm();
    const { data: startPortOptions } = useGetStartPortQuery(1);
    const { data: endPortOptions } = useGetEndPortQuery(1);

    const [dataSource, setDataSource] = useState({ data: [], total_page: 0 });
    const [dataSourceAll, setDataSourceAll] = useState([]);
    const [tableLoading, setTableLoading] = useState(false);
    const [searchLoading, setSearchLoading] = useState(false);
    const [submittingId, setSubmittingId] = useState(null);
    const [disableIds, setDisableIds] = useState([]);

    // 分页状态
    const [pagination, setPagination] = useState({
        current: 1,
        pageSize: 100,
        total: 0,
        showSizeChanger: true,
        showQuickJumper: true,
        pageSizeOptions: [10, 20, 50, 100, 200],
        showTotal: (total, range) => `共 ${total} 条`,
    });

    // 排序状态管理
    const [sortQueue, setSortQueue] = useState([]);

    // 获取数据
    const fetchData = async (page = 1, pageSize = 100) => {
        setTableLoading(true);
        try {
            let params = getListPayload({ form });
            params.page = page;
            params.per_page = pageSize;

            const response = await ajax.post('/book/check', params);

            if (response.data) {
                const formattedData = response.data.map((item) => {
                    // 处理开航时间
                    let earlytimeFormatted = '';
                    if (item.earlytime) {
                        earlytimeFormatted = dayjs(item.earlytime).format('YYYY-MM-DD');
                    }
                    // 处理到港时间
                    let arriveTimeFormatted = '';
                    if (item.arrive_time) {
                        arriveTimeFormatted = dayjs(item.arrive_time).format('YYYY-MM-DD');
                    }
                    // 计算航程
                    let voyageDays = null;
                    if (item.earlytime && item.arrive_time) {
                        const diff = dayjs(item.arrive_time).diff(dayjs(item.earlytime), 'day');
                        voyageDays = isNaN(diff) ? null : diff;
                    }
                    return {
                        ...item,
                        earlytime: earlytimeFormatted,
                        arrive_time: arriveTimeFormatted,
                        voyage_days: voyageDays,
                    };
                });

                setDataSource({
                    data: formattedData,
                    total_page: response.total_page || response.data.length,
                });

                setPagination((prev) => ({
                    ...prev,
                    current: page,
                    pageSize: pageSize,
                    total: response.total_page || response.data.length,
                }));
            }
        } catch (err) {
            console.log(err);
            message.error('获取数据失败');
        } finally {
            setTableLoading(false);
        }
    };

    // 获取全部数据用于导出
    const fetchAllData = async () => {
        try {
            let params = getListPayload({ form });
            params.per_page = 10000;

            const response = await ajax.post('/book/check', params);
            if (response.data) {
                const formattedAllData = response.data.map((item) => {
                    let earlytimeFormatted = '';
                    if (item.earlytime) {
                        earlytimeFormatted = dayjs(item.earlytime).format('YYYY-MM-DD');
                    }
                    let arriveTimeFormatted = '';
                    if (item.arrive_time) {
                        arriveTimeFormatted = dayjs(item.arrive_time).format('YYYY-MM-DD');
                    }
                    let voyageDays = null;
                    if (item.earlytime && item.arrive_time) {
                        const diff = dayjs(item.arrive_time).diff(dayjs(item.earlytime), 'day');
                        voyageDays = isNaN(diff) ? null : diff;
                    }
                    return {
                        ...item,
                        earlytime: earlytimeFormatted,
                        arrive_time: arriveTimeFormatted,
                        voyage_days: voyageDays,
                    };
                });
                setDataSourceAll(formattedAllData);
            }
        } catch (err) {
            console.log(err);
        }
    };

    // 搜索处理
    const handleSearch = async () => {
        setSearchLoading(true); // 设置搜索按钮为加载状态
        try {
            setPagination((prev) => ({ ...prev, current: 1 }));
            await Promise.all([fetchData(1, pagination.pageSize), fetchAllData()]);
        } catch (err) {
            console.log(err);
        } finally {
            setSearchLoading(false); // 搜索完成，取消加载状态
        }
    };

    // 处理排序点击
    const handleSort = (field, order) => {
        setSortQueue((prevQueue) => {
            const existingIndex = prevQueue.findIndex((item) => item.field === field);
            let newQueue = [...prevQueue];

            if (existingIndex >= 0) {
                if (order) {
                    newQueue[existingIndex] = { ...newQueue[existingIndex], order };
                } else {
                    newQueue.splice(existingIndex, 1);
                }
            } else if (order) {
                newQueue.push({ field, order });
            }

            return newQueue;
        });
    };

    // 获取列的排序状态
    const getColumnSortOrder = (field) => {
        const sortItem = sortQueue.find((item) => item.field === field);
        return sortItem ? sortItem.order : null;
    };

    // 应用多层级排序
    const sortedDataSource = useMemo(() => {
        if (!dataSource?.data || sortQueue.length === 0) {
            return dataSource.data || [];
        }

        return [...dataSource.data].sort((a, b) => {
            for (const sortItem of sortQueue) {
                const { field, order } = sortItem;

                let compareResult = 0;
                const aValue = a[field];
                const bValue = b[field];

                switch (field) {
                    case 'username':
                    case 'is_roll':
                    case 'origin_location':
                    case 'destination_location':
                    case 'destination_service_mode':
                    case 'box_type':
                        compareResult = (aValue || '').localeCompare(bValue || '');
                        break;

                    case 'vessel_name':
                    case 'price':
                    case 'booking_number':
                    // case 'box_number':
                    case 'free_day':
                    case 'is_instant_confirmation':
                    case 'capacity_hard_stop_indicator':
                        compareResult = (aValue || 0) - (bValue || 0);
                        break;

                    case 'earlytime':
                    case 'arrive_time':
                    case 'booktime':
                    case 'endtime':
                    case 'update_time':
                    case 'voyage_days':
                        compareResult = new Date(aValue || 0) - new Date(bValue || 0);
                        break;

                    default:
                        compareResult = 0;
                }

                if (order === 'descend') {
                    compareResult = -compareResult;
                }

                if (compareResult !== 0) {
                    return compareResult;
                }
            }
            return 0;
        });
    }, [dataSource, sortQueue]);

    // 清除所有排序
    const clearAllSorts = () => {
        setSortQueue([]);
    };

    const orderStatusOptions = [
        { label: '未提交订单', value: 0 },
        { label: '已提交', value: 1 },
        { label: '超时', value: 2 },
        { label: '正在锁定', value: 3 },
        { label: '退关', value: 4 },
    ];

    let tableTitle = [
        '账号',
        '开航时间',
        '到港时间',
        '起始港',
        '目的港',
        '目的港服务模式',
        '箱型',
        '船名',
        '航程（天）',
        // '箱量',
        '提单号',
        '价格',
        'is_roll',
        '返回值2',
        '下单时间',
        '截止提交时间',
        '提交时间',
        '状态',
        'ID',
        '返回值1',
        '免用箱',
    ];
    let tableFilter = [
        'username',
        'earlytime',
        'arrive_time',
        'origin_location',
        'destination_location',
        'destination_service_mode',
        'box_type',
        // 'box_number',
        'vessel_name',
        'voyage_days',
        'booking_number',
        'price',
        'is_roll',
        'capacity_hard_stop_indicator',
        'booktime',
        'endtime',
        'update_time',
        'is_book',
        'id',
        'is_instant_confirmation',
        'free_day',
    ];

    const ExcelItems = [
        { key: '1', label: '导出当前页' },
        { key: '2', label: '导出全部页' },
    ];

    // 自定义排序渲染标题组件
    const SortableTitle = ({ field, children }) => {
        const sortItem = sortQueue.find((item) => item.field === field);
        const sortIndex = sortQueue.findIndex((item) => item.field === field) + 1;

        return (
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                {children}
                {sortItem && (
                    <Tag color="blue" style={{ marginLeft: 4, fontSize: 10, minWidth: 16, height: 16, lineHeight: '14px' }}>
                        {sortIndex}
                    </Tag>
                )}
            </div>
        );
    };

    // 表格列配置
    const columns = [
        {
            title: '下单',
            key: 'actions',
            width: 120,
            align: 'center',
            render: (data) => {
                if (data.is_book === 0) {
                    const disabled = disableIds.includes(data.id);
                    const showPrice = !disabled && data.price != null;
                    return (
                        <Button
                            loading={data.id === submittingId}
                            onClick={() => handleOrderSubmit(data.id)}
                            disabled={disabled}
                            style={showPrice ? { color: '#ee7613', fontWeight: 'bold' } : {}}
                        >
                            {disabled ? '下单完成' : `${data.price} / ${data.box_number ? data.box_number : ''}`}
                        </Button>
                    );
                }
                if (data.is_book === 1) {
                    const disabled = disableIds.includes(data.id);
                    return (
                        <Button loading={data.id === submittingId} onClick={() => handleShutOut(data.id)} disabled={disabled}>
                            {disabled ? '退关完成' : '退关'}
                        </Button>
                    );
                }
                return null;
            },
        },

        {
            title: <SortableTitle field="username">账号</SortableTitle>,
            dataIndex: 'username',
            width: 120,
            align: 'left',
            sorter: true,
            sortOrder: getColumnSortOrder('username'),
        },

        {
            title: <SortableTitle field="earlytime">开航时间</SortableTitle>,
            dataIndex: 'earlytime',
            width: 200,
            align: 'center',
            sorter: true,
            sortOrder: getColumnSortOrder('earlytime'),
        },
        {
            title: <SortableTitle field="arrive_time">到港时间</SortableTitle>,
            dataIndex: 'arrive_time',
            width: 200,
            align: 'center',
            sorter: true,
            sortOrder: getColumnSortOrder('arrive_time'),
        },

        {
            title: <SortableTitle field="origin_location">起始港</SortableTitle>,
            dataIndex: 'origin_location',
            width: 120,
            align: 'center',
            sorter: true,
            sortOrder: getColumnSortOrder('origin_location'),
        },
        {
            title: <SortableTitle field="destination_location">目的港</SortableTitle>,
            dataIndex: 'destination_location',
            width: 120,
            align: 'center',
            sorter: true,
            sortOrder: getColumnSortOrder('destination_location'),
        },
        {
            title: <SortableTitle field="destination_service_mode">目的港服务模式</SortableTitle>,
            dataIndex: 'destination_service_mode',
            width: 150,
            align: 'center',
            sorter: true,
            sortOrder: getColumnSortOrder('destination_service_mode'),
        },
        {
            title: <SortableTitle field="box_type">箱型</SortableTitle>,
            dataIndex: 'box_type',
            width: 160,
            align: 'center',
            sorter: true,
            sortOrder: getColumnSortOrder('box_type'),
            render: (text, record) => {
                const breakdown = record.box_price_breakdown;
                if (breakdown?.items?.length > 0) {
                    const content = (
                        <div style={{ padding: '4px 0' }}>
                            <table style={{ borderCollapse: 'collapse', fontSize: '13px' }}>
                                <thead>
                                    <tr style={{ borderBottom: '1px solid #e8e8e8' }}>
                                        <th style={{ padding: '4px 12px 4px 0', textAlign: 'left' }}>箱型</th>
                                        <th style={{ padding: '4px 12px', textAlign: 'center' }}>数量</th>
                                        <th style={{ padding: '4px 0 4px 12px', textAlign: 'right' }}>价格</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {breakdown.items.map((item, index) => (
                                        <tr key={index}>
                                            <td style={{ padding: '4px 12px 4px 0' }}>{item.box_type || '-'}</td>
                                            <td style={{ padding: '4px 12px', textAlign: 'center' }}>{item.quantity ?? '-'}</td>
                                            <td style={{ padding: '4px 0 4px 12px', textAlign: 'right' }}>{item.unit_price ?? '-'}</td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    );

                    return (
                        <Popover content={content} trigger="click" placement="right" overlayStyle={{ maxWidth: 360 }}>
                            <span style={{ color: '#1890ff', textDecoration: 'underline dotted', cursor: 'pointer' }}>{text}</span>
                        </Popover>
                    );
                }
                return text;
            },
        },
        // {
        //     title: <SortableTitle field="box_number">箱量</SortableTitle>,
        //     dataIndex: 'box_number',
        //     width: 160,
        //     align: 'center',
        //     sorter: true,
        //     sortOrder: getColumnSortOrder('box_number'),
        // },
        {
            title: <SortableTitle field="vessel_name">船名</SortableTitle>,
            dataIndex: 'vessel_name',
            width: 160,
            align: 'center',
            sorter: true,
            sortOrder: getColumnSortOrder('vessel_name'),
        },
        {
            title: <SortableTitle field="voyage_days">航程（天）</SortableTitle>,
            dataIndex: 'voyage_days',
            width: 100,
            align: 'center',
            sorter: true,
            sortOrder: getColumnSortOrder('voyage_days'),
            render: (value) => (value !== undefined && value !== null ? `${value}` : '-'),
        },
        {
            title: <SortableTitle field="booking_number">提单号</SortableTitle>,
            dataIndex: 'booking_number',
            width: 120,
            align: 'center',
            sorter: true,
            sortOrder: getColumnSortOrder('booking_number'),
        },
        {
            title: <SortableTitle field="price">价格</SortableTitle>,
            dataIndex: 'price',
            width: 120,
            align: 'center',
            sorter: true,
            sortOrder: getColumnSortOrder('price'),
        },
        {
            title: <SortableTitle field="is_roll">is_roll</SortableTitle>,
            dataIndex: 'is_roll',
            width: 120,
            align: 'center',
            sorter: true,
            sortOrder: getColumnSortOrder('is_roll'),
        },

        {
            title: '返回值2',
            dataIndex: 'capacity_hard_stop_indicator',
            width: 100,
            align: 'right',
            render: (value) => {
                const searchIsBook = form.getFieldValue('is_book');
                const isBookSubmitted = searchIsBook === 1;
                const isNot200 = value !== 200 && value !== '200';
                const shouldHighlight = isBookSubmitted && isNot200;

                return <span style={{ color: shouldHighlight ? '#ff8c00' : undefined }}>{value ?? '-'}</span>;
            },
        },
        {
            title: <SortableTitle field="booktime">下单时间</SortableTitle>,
            dataIndex: 'booktime',
            width: 200,
            align: 'right',
            sorter: true,
            sortOrder: getColumnSortOrder('booktime'),
        },
        {
            title: <SortableTitle field="endtime">截止提交时间</SortableTitle>,
            dataIndex: 'endtime',
            width: 200,
            align: 'right',
            sorter: true,
            sortOrder: getColumnSortOrder('endtime'),
        },
        {
            title: <SortableTitle field="update_time">提交时间</SortableTitle>,
            dataIndex: 'update_time',
            width: 200,
            align: 'right',
            sorter: true,
            sortOrder: getColumnSortOrder('update_time'),
        },
        {
            title: '状态',
            dataIndex: 'is_book',
            width: 120,
            align: 'center',
            render: (value) => getLabelFromMap({ map: orderStatusOptions, value }),
        },

        {
            title: 'ID',
            dataIndex: 'id',
            width: 60,
            align: 'center',
        },
        {
            title: '返回值1',
            dataIndex: 'is_instant_confirmation',
            width: 100,
            align: 'right',
        },
        {
            title: '免用箱',
            dataIndex: 'free_day',
            width: 100,
            align: 'center',
        },
    ];

    // 表格变化处理
    const handleTableChange = (newPagination, filters, sorter, extra) => {
        // 处理分页变化
        if (extra.action === 'paginate') {
            fetchData(newPagination.current, newPagination.pageSize);
            return;
        }

        // 处理排序变化
        if (extra.action === 'sort') {
            if (sorter && sorter.column) {
                const { field } = sorter;
                const currentOrder = getColumnSortOrder(field);

                let newOrder;
                if (currentOrder === 'ascend') {
                    newOrder = 'descend';
                } else if (currentOrder === 'descend') {
                    newOrder = null;
                } else {
                    newOrder = 'ascend';
                }

                handleSort(field, newOrder);
            } else if (sorter && sorter.order === undefined && sorter.field) {
                handleSort(sorter.field, null);
            }
        }
    };

    // 提交订单
    const handleOrderSubmit = async (id) => {
        setSubmittingId(id);
        try {
            const { bool_status, data } = await ajax.post('book/order', { id }, { rawResponse: true });
            setSubmittingId(null);
            message.info(data);
            if (bool_status) setDisableIds([...disableIds, id]);
        } catch (err) {
            console.log(err);
            setSubmittingId(null);
        }
    };

    // 退关
    const handleShutOut = async (id) => {
        setSubmittingId(id);
        try {
            await ajax.post('/book/out', { id });
            message.info('成功退关');
            setDisableIds([...disableIds, id]);
        } catch (err) {
            console.log(err);
        } finally {
            setSubmittingId(null);
        }
    };

    // 导出Excel
    const onMenuClick = (e) => {
        let tableData = [];
        let options = {};

        if (e.key === '1') {
            // 导出当前页（排序后的数据）
            const exportData = sortedDataSource.length > 0 ? sortedDataSource : dataSource.data;
            exportData.forEach((item) => {
                tableData.push({
                    id: item.id,
                    username: item.username,
                    arrive_time: item.arrive_time,
                    origin_location: item.origin_location,
                    destination_location: item.destination_location,
                    destination_service_mode: item.destination_service_mode,
                    box_type: item.box_type,
                    vessel_name: item.vessel_name,
                    voyage_days: item.voyage_days,
                    // box_number: item.box_number,
                    booking_number: item.booking_number,
                    free_day: item.free_day,
                    price: item.price,
                    is_roll: item.is_roll,
                    is_instant_confirmation: item.is_instant_confirmation,
                    capacity_hard_stop_indicator: item.capacity_hard_stop_indicator,
                    earlytime: item.earlytime,
                    booktime: item.booktime,
                    endtime: item.endtime,
                    update_time: item.update_time,
                    is_book: item.is_book,
                });
            });
        } else if (e.key === '2') {
            // 导出全部数据
            dataSourceAll.forEach((item) => {
                tableData.push({
                    id: item.id,
                    username: item.username,
                    arrive_time: item.arrive_time,
                    voyage_days: item.voyage_days,
                    vessel_name: item.vessel_name,
                    is_roll: item.is_roll,
                    origin_location: item.origin_location,
                    destination_location: item.destination_location,
                    destination_service_mode: item.destination_service_mode,
                    box_type: item.box_type,
                    // box_number: item.box_number,
                    booking_number: item.booking_number,
                    free_day: item.free_day,
                    price: item.price,
                    is_instant_confirmation: item.is_instant_confirmation,
                    capacity_hard_stop_indicator: item.capacity_hard_stop_indicator,
                    earlytime: item.earlytime,
                    booktime: item.booktime,
                    endtime: item.endtime,
                    update_time: item.update_time,
                    is_book: item.is_book,
                });
            });
        }

        // 转换状态值
        tableData.forEach((item) => {
            const statusMap = {
                0: '未提交订单',
                1: '已提交',
                2: '超时',
                3: '正在锁定',
                4: '退关',
            };
            item.is_book = statusMap[item.is_book] || item.is_book;
        });

        options.fileName = '订单列表';
        options.datas = [
            {
                sheetData: tableData,
                sheetName: 'sheet',
                sheetFilter: tableFilter,
                sheetHeader: tableTitle,
            },
        ];

        const toExcel = new ExportJsonExcel(options);
        toExcel.saveExcel();
    };

    // 初始化加载数据
    useEffect(() => {
        fetchData(1, 100);
        fetchAllData();
    }, []);

    return (
        <div className="ax-page-list">
            <PageHeader title="订单列表" sticky></PageHeader>
            <Card className="ax-search-card">
                <Form form={form} autoComplete="off">
                    <Row gutter={SEARCH_ITEM_GUTTER}>
                        <Col {...SEARCH_ITEM_COL}>
                            <Form.Item label="起始" name="origin_location">
                                <Select
                                    options={createJsonOptions(startPortOptions)}
                                    filterOption={(input, option) => option.value.toLowerCase().indexOf(input.toLowerCase()) === 0}
                                    showSearch
                                    allowClear
                                ></Select>
                            </Form.Item>
                        </Col>
                        <Col {...SEARCH_ITEM_COL}>
                            <Form.Item label="目的" name="destination_location">
                                <Select
                                    options={createJsonOptions(endPortOptions)}
                                    filterOption={(input, option) => option.value.toLowerCase().indexOf(input.toLowerCase()) === 0}
                                    showSearch
                                    allowClear
                                ></Select>
                            </Form.Item>
                        </Col>
                        <Col {...SEARCH_ITEM_COL}>
                            <Form.Item label="箱型" name="is_time_out">
                                <Select
                                    options={createJsonOptions(['20 Dry Standard', '40 Dry High', '40 Reefer High', '45 Dry High'])}
                                    showSearch
                                    allowClear
                                ></Select>
                            </Form.Item>
                        </Col>
                        <Col {...SEARCH_ITEM_COL}>
                            <Form.Item label="订单状态" name="is_book">
                                <Select options={orderStatusOptions} allowClear></Select>
                            </Form.Item>
                        </Col>
                        <Col {...SEARCH_ITEM_COL}>
                            <Form.Item label="开始时间" name="start_time">
                                <DatePicker showTime={{ defaultValue: dayjs('00:00:00', 'HH:mm:ss') }} style={{ width: '100%' }} />
                            </Form.Item>
                        </Col>
                        <Col {...SEARCH_ITEM_COL}>
                            <Form.Item label="结束时间" name="end_time">
                                <DatePicker showTime={{ defaultValue: dayjs('00:00:00', 'HH:mm:ss') }} style={{ width: '100%' }} />
                            </Form.Item>
                        </Col>
                        <Col {...SEARCH_ITEM_COL}>
                            <Form.Item label="开航时间" name="earlytime">
                                <DatePicker format="YYYY-MM-DD" style={{ width: '100%' }} />
                            </Form.Item>
                        </Col>
                        <Col {...SEARCH_ITEM_COL}>
                            <Form.Item label="用户名" name="username">
                                <Input />
                            </Form.Item>
                        </Col>
                        <Col {...SEARCH_ITEM_COL}>
                            <Form.Item label="船名" name="vessel_name">
                                <Input />
                            </Form.Item>
                        </Col>
                        <Col {...SEARCH_ITEM_COL} style={{ display: 'flex', alignItems: 'flex-start' }}>
                            <Form.Item label="订单分组" name="list_type" style={{ flex: 1, marginRight: 16 }}>
                                <Input />
                            </Form.Item>
                            <Space>
                                <Button
                                    type="primary"
                                    onClick={handleSearch}
                                    loading={searchLoading} // 添加加载状态
                                >
                                    搜索
                                </Button>
                                <Dropdown menu={{ items: ExcelItems, onClick: onMenuClick }}>
                                    <Button>
                                        <Space>Excel导出</Space>
                                    </Button>
                                </Dropdown>
                            </Space>
                        </Col>
                        <Col span={24}>
                            <div style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '8px 0' }}>
                                <span style={{ fontWeight: 'bold' }}>排序顺序:</span>
                                {sortQueue.length > 0 ? (
                                    <>
                                        {sortQueue.map((item, index) => (
                                            <Tag key={item.field} color="blue" closable onClose={() => handleSort(item.field, null)}>
                                                {index + 1}. {item.field} {item.order === 'ascend' ? '↑' : '↓'}
                                            </Tag>
                                        ))}
                                        <Button size="small" onClick={clearAllSorts}>
                                            清除所有排序
                                        </Button>
                                    </>
                                ) : (
                                    <span style={{ color: '#999' }}>暂无排序（点击列标题添加排序）</span>
                                )}
                            </div>
                        </Col>
                    </Row>
                </Form>
            </Card>
            <Card className="ax-table-card">
                <Table
                    columns={columns}
                    dataSource={sortedDataSource.length > 0 ? sortedDataSource : dataSource.data}
                    rowKey="id"
                    pagination={pagination}
                    loading={tableLoading}
                    onChange={handleTableChange}
                    showSorterTooltip={true}
                    scroll={{ x: 2000 }}
                />
            </Card>
        </div>
    );
}
